import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Image as ImageIcon, Bookmark, MessageCircle, Share2, TrendingUp, Eye } from "lucide-react";

const statusConfig = {
  winner: {
    label: "Winner",
    variant: "default",
    bgColor: "bg-green-50 dark:bg-green-950",
    borderColor: "border-green-500",
  },
  loser: {
    label: "Loser",
    variant: "destructive",
    bgColor: "bg-red-50 dark:bg-red-950",
    borderColor: "border-destructive",
  },
  testing: {
    label: "Testing",
    variant: "secondary",
    bgColor: "bg-muted",
    borderColor: "border-border",
  },
};

export default function CreativeQualityGrid({ creatives }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {creatives.slice(0, 6).map((creative) => {
        const status = statusConfig[creative.creative_status] || statusConfig.testing;
        
        return (
          <Card key={creative.campaign_id} className={`${status.borderColor} border-2 ${status.bgColor}`}>
            <CardContent className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {creative.creative_type === 'video' ? (
                      <Play className="h-5 w-5 text-primary" />
                    ) : (
                      <ImageIcon className="h-5 w-5 text-muted-foreground" />
                    )}
                    <Badge variant={creative.is_ugc ? "default" : "outline"}>
                      {creative.is_ugc ? "UGC" : creative.creative_type}
                    </Badge>
                    {creative.has_captions && (
                      <Badge variant="outline">CC</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-foreground line-clamp-2">
                    {creative.campaign_name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {creative.hook || 'No hook defined'}
                  </p>
                </div>
                <Badge variant={status.variant} className="ml-2">{status.label}</Badge>
              </div>

              {/* Creative Preview */}
              {creative.creative_url && (
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                  {creative.creative_type === 'video' ? (
                    <div className="relative w-full h-full bg-black/5 flex items-center justify-center">
                      <Play className="h-12 w-12 text-muted-foreground" />
                    </div>
                  ) : (
                    <ImageIcon className="h-12 w-12 text-muted-foreground" />
                  )}
                </div>
              )}

              {/* Quality Metrics */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground">📊 Quality Metrics</h4>
                <div className="grid grid-cols-2 gap-3">
                  <QualityMetric 
                    icon={TrendingUp}
                    label="Engagement Rate" 
                    value={`${creative.avg_engagement_rate}%`}
                    status={creative.avg_engagement_rate > 3.5 ? 'good' : creative.avg_engagement_rate < 1.5 ? 'bad' : 'neutral'}
                  />
                  <QualityMetric 
                    icon={Bookmark}
                    label="Saves" 
                    value={creative.total_saves}
                    subtitle="high intent!"
                  />
                  <QualityMetric 
                    icon={MessageCircle}
                    label="Comments" 
                    value={creative.total_comments}
                  />
                  <QualityMetric 
                    icon={Share2}
                    label="Shares" 
                    value={creative.total_shares}
                  />
                </div>

                {creative.creative_type === 'video' && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <QualityMetric 
                      icon={Eye}
                      label="Hook Retention" 
                      value={`${creative.hook_retention_rate}%`}
                      subtitle="watched 3s+"
                      status={creative.hook_retention_rate > 70 ? 'good' : 'neutral'}
                    />
                    <QualityMetric 
                      icon={Play}
                      label="Avg Watch Time" 
                      value={`${creative.avg_watch_time_sec}s`}
                      subtitle={creative.video_length_sec ? `of ${creative.video_length_sec}s` : ''}
                    />
                  </div>
                )}
              </div>

              {/* Performance */}
              <div className="space-y-3 pt-3 border-t">
                <h4 className="text-sm font-semibold text-foreground">💰 Performance</h4>
                <div className="grid grid-cols-3 gap-3">
                  <PerformanceMetric 
                    label="CPM" 
                    value={`$${creative.avg_cpm}`}
                    status={creative.avg_cpm < 25 ? 'good' : creative.avg_cpm > 40 ? 'bad' : 'neutral'}
                  />
                  <PerformanceMetric 
                    label="Reach" 
                    value={formatNumber(creative.reach)}
                  />
                  <PerformanceMetric 
                    label="Spend" 
                    value={`$${formatNumber(creative.spend)}`}
                  />
                </div>
              </div>

              {/* Quality Score */}
              <div className="pt-3 border-t flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Quality Score</p>
                  <p className="text-2xl font-bold text-primary">{creative.creative_quality_score}</p>
                </div>
                <div className="flex gap-2">
                  {creative.creative_status === 'winner' && (
                    <Button size="sm" variant="default">Scale this!</Button>
                  )}
                  {creative.creative_status === 'loser' && (
                    <Button size="sm" variant="destructive">Pause</Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function QualityMetric({ icon: Icon, label, value, subtitle, status }) {
  const statusColors = {
    good: 'text-green-600 dark:text-green-400',
    bad: 'text-destructive',
    neutral: 'text-foreground',
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <p className={`text-lg font-bold ${statusColors[status] || 'text-foreground'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

function PerformanceMetric({ label, value, status }) {
  const statusColors = {
    good: 'text-green-600 dark:text-green-400',
    bad: 'text-destructive',
    neutral: 'text-foreground',
  };

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${statusColors[status] || 'text-foreground'}`}>
        {value}
      </p>
    </div>
  );
}

function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}
